import { JSX, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";

type LimitOption = { value: number | string; label: string };

type MetaFilter = {
  key: string;
  label?: string;
  default?: number | string;
  options?: LimitOption[];
};

// Absolute TAT per payer for both measures ("value" mirrors the median for
// backward compatibility). null = not enough data for that payer.
type BarPoint = {
  name: string;
  value?: number | null;
  median_tat_days?: number | null;
  mean_tat_days?: number | null;
};

type Metadata = {
  top_n?: number | string;
  total_payers?: number;
  payers_returned?: number;
  average_payer_median_tat_days?: number | null;
  average_payer_mean_tat_days?: number | null;
  fence_stage?: string;
  filters?: MetaFilter[];
  [key: string]: unknown;
};

type AverageBarData = {
  data?: BarPoint[];
  metadata?: Metadata;
};

type AverageBarWidgetProps = {
  isLoading: boolean;
  data: unknown;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

type Measure = "median" | "mean";

const MEASURES: Array<{ key: Measure; label: string }> = [
  { key: "median", label: "Median" },
  { key: "mean", label: "Mean" },
];

const FALLBACK_LIMIT_OPTIONS: LimitOption[] = [
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: 50, label: "Top 50" },
  { value: "all", label: "All" },
];

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

const fmtDays = (val: number | null): string =>
  val === null ? "–" : `${val.toFixed(1)}d`;

const unwrap = (raw: unknown): AverageBarData => {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.data)) return obj as AverageBarData;
  return obj as AverageBarData;
};

function getBarColor(deviation: number, refAvg: number): string {
  if (deviation <= 0) return "#16a34a";
  if (deviation <= refAvg * 0.15) return "#ca8a04";
  return "#dc2626";
}

export default function AverageBarWidget({
  isLoading,
  data,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: AverageBarWidgetProps): JSX.Element {
  const [limit, setLimit] = useState<number | string | null>(null);
  // Which TAT statistic the bars (and the reference line) show. Median is the
  // default; Mean is offered so a median↔mean gap exposes skewed payers.
  const [measure, setMeasure] = useState<Measure>("median");

  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading: isFetching } = useQuery({
    queryKey: [
      "widget",
      dashboardName,
      widgetTemplateId,
      appliedFilters,
      limit,
    ],
    queryFn: ({ signal }) =>
      getDashboardWidgetDataApi(
        dashboardName!,
        String(widgetTemplateId!),
        {
          filters: appliedFilters,
          widget_filters: limit === null ? undefined : { top_n: limit },
        },
        { signal },
      ),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const parsed = useMemo(
    () => unwrap(canFetch ? queryData : data),
    [canFetch, queryData, data],
  );
  const meta = parsed.metadata;
  const points = useMemo(() => parsed.data ?? [], [parsed]);

  const reference: Record<Measure, number | null> = {
    median: isNumeric(meta?.average_payer_median_tat_days)
      ? meta.average_payer_median_tat_days
      : null,
    mean: isNumeric(meta?.average_payer_mean_tat_days)
      ? meta.average_payer_mean_tat_days
      : null,
  };
  const refAvg = reference[measure];

  const tatOf = (pt: BarPoint): number | null => {
    const v = measure === "median" ? pt.median_tat_days : pt.mean_tat_days;
    return isNumeric(v) ? v : null;
  };

  const topNFilter = meta?.filters?.find((f) => f.key === "top_n");
  const limitOptions = topNFilter?.options?.length
    ? topNFilter.options
    : FALLBACK_LIMIT_OPTIONS;
  const limitValue = limit ?? topNFilter?.default ?? 10;

  // Scale to the slowest payer OR the reference line, whichever is larger, so
  // the dashed line always lands inside the track.
  const { maxVal, avgPct } = useMemo(() => {
    let mx = 0;
    for (const pt of points) {
      const v = measure === "median" ? pt.median_tat_days : pt.mean_tat_days;
      if (isNumeric(v) && v > mx) mx = v;
    }
    const ref =
      measure === "median"
        ? meta?.average_payer_median_tat_days
        : meta?.average_payer_mean_tat_days;
    if (isNumeric(ref) && ref > mx) mx = ref;
    if (mx === 0) mx = 1;
    return {
      maxVal: mx,
      avgPct: isNumeric(ref) ? (ref / mx) * 100 : 0,
    };
  }, [points, measure, meta]);

  if (canFetch ? isFetching : isLoading) {
    return (
      <div className="placeholder-glow h-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  const limitSelect = canFetch ? (
    <select
      className="form-select form-select-sm"
      style={{
        width: "auto",
        fontSize: 11,
        minWidth: 72,
        padding: "1px 24px 1px 6px",
        cursor: "pointer",
        marginLeft: "auto",
        flexShrink: 0,
      }}
      value={String(limitValue)}
      onChange={(e) => {
        const v = e.target.value;
        setLimit(v === "all" ? v : Number(v));
      }}
      aria-label="How many rows to show"
    >
      {limitOptions.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  ) : null;

  if (points.length === 0) {
    return (
      <div className="average-bar">
        <p className="text-muted small mb-0">No data available.</p>
      </div>
    );
  }

  const totalPayers = meta?.total_payers;
  const summary =
    totalPayers != null
      ? `Showing ${limitValue === "all" ? "all" : `top ${limitValue}`} of ${totalPayers.toLocaleString()} payers`
      : null;

  const trackStyle = { "--avg-pct": `${avgPct}%` } as React.CSSProperties;

  return (
    <div className="average-bar">
      <div className="average-bar__meta">
        {summary && (
          <span className="average-bar__summary">{summary}</span>
        )}
        {meta?.fence_stage && (
          <span className="average-bar__stage">{meta.fence_stage}</span>
        )}
        <div
          className="measure-toggle"
          role="group"
          aria-label="TAT statistic to display"
        >
          {MEASURES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={measure === m.key ? "is-active" : ""}
              aria-pressed={measure === m.key}
              onClick={() => setMeasure(m.key)}
            >
              {m.label}
              <span className="measure-toggle__val">
                {fmtDays(reference[m.key])}
              </span>
            </button>
          ))}
        </div>
        {limitSelect}
      </div>

      <div className="average-bar__chart">
        {/* avg label row — tag flips to the line's left when the line sits in
            the right third of the track, so it never spills past the edge */}
        <div className="average-bar__bar-row average-bar__bar-row--label">
          <span className="average-bar__bar-label" />
          <span className="average-bar__bar-track" style={trackStyle}>
            {refAvg !== null && (
              <span
                className={`average-bar__avg-tag${
                  avgPct > 70 ? " average-bar__avg-tag--left" : ""
                }`}
              >
                avg {refAvg.toFixed(1)}d
              </span>
            )}
          </span>
          <span className="average-bar__bar-value" />
        </div>

        {points.map((pt) => {
          const actualTat = tatOf(pt);

          if (actualTat === null) {
            return (
              <div key={pt.name} className="average-bar__bar-row">
                <span className="average-bar__bar-label" title={pt.name}>
                  {pt.name}
                </span>
                <span className="average-bar__bar-track" style={trackStyle} />
                <span
                  className="average-bar__bar-value"
                  style={{ color: "#94a3b8" }}
                >
                  N/A
                </span>
              </div>
            );
          }

          const widthPct = (actualTat / maxVal) * 100;
          const color =
            refAvg !== null
              ? getBarColor(actualTat - refAvg, refAvg)
              : "#64748b";

          return (
            <div key={pt.name} className="average-bar__bar-row">
              <span className="average-bar__bar-label" title={pt.name}>
                {pt.name}
              </span>
              <span className="average-bar__bar-track" style={trackStyle}>
                <span
                  className="average-bar__bar-fill"
                  style={{
                    width: `${widthPct}%`,
                    background: color,
                  }}
                />
              </span>
              <span className="average-bar__bar-value" style={{ color }}>
                {actualTat.toFixed(1)}d
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
